import React, { createContext, useContext, useState, useCallback, useRef, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { set, get, cloneDeep } from 'lodash'

/**
 * FormContext - Custom React Context for form state management
 * Replaces Formik with a simpler, more maintainable solution
 * Manages form values, errors, touched state, validation, and submission state
 */
const FormContext = createContext()

/**
 * Hook to access form context
 * Must be used within a FormProvider component
 * @returns {Object} Form context value with values, errors, and form methods
 */
export const useFormContext = () => {
  const context = useContext(FormContext)
  if (!context) {
    throw new Error('useFormContext must be used within FormProvider')
  }
  return context
}

/**
 * FormProvider - Provides form context to all child components
 * Manages form state including values, errors, touched fields, and validation
 * 
 * @param {ReactNode} children - Child components that can use form context
 * @param {Object} initialValues - Initial form values
 */
export const FormProvider = ({ children, initialValues }) => {
  // Form state
  const [values, setValues] = useState(initialValues || {}) // Current form values
  const [errors, setErrors] = useState({}) // Validation errors
  const [touched, setTouched] = useState({}) // Track which fields have been touched
  const [submitCount, setSubmitCount] = useState(0) // Number of form submission attempts
  const [isSubmitting, setIsSubmitting] = useState(false) // Form submission in progress
  const [isValidating, setIsValidating] = useState(false) // Form validation in progress
  
  // Use a ref to store the latest values so validateForm can always access current values
  // This prevents stale closure issues when validateForm is called immediately after setFieldValue
  const valuesRef = useRef(initialValues || {})
  
  // Keep ref in sync with state (but setFieldValue updates it immediately before state update)
  useEffect(() => {
    valuesRef.current = values
  }, [values])

  /**
   * Set a field value (supports nested paths like 'contactInfo.firstName')
   * Uses lodash.set to handle nested object paths
   * @param {string} name - Field name (can be nested like 'contactInfo.email')
   * @param {*} value - Value to set
   */
  const setFieldValue = useCallback((name, value) => {
    // Use the current state values to ensure we have the latest
    setValues((prevValues) => {
      const currentValues = prevValues || {}
      const updatedValues = cloneDeep(currentValues)
      // Use lodash.set which handles nested paths like 'contactInfo.email' and creates objects as needed
      set(updatedValues, name, value)
      // Update ref synchronously BEFORE returning, so validateForm can access the latest value immediately
      valuesRef.current = updatedValues
      return updatedValues
    })
  }, [])

  /**
   * Set a field error
   * @param {string} name - Field name (can be nested)
   * @param {string|undefined} error - Error message, or undefined/null to clear error
   */
  const setFieldError = useCallback((name, error) => {
    setErrors((prev) => {
      if (error === undefined || error === null) {
        const newErrors = cloneDeep(prev)
        // Remove the error (supports nested paths)
        if (name.includes('.')) {
          const parts = name.split('.')
          let current = newErrors
          for (let i = 0; i < parts.length - 1; i++) {
            if (!current[parts[i]]) {
              return prev // Path doesn't exist, no change needed
            }
            current = current[parts[i]]
          }
          delete current[parts[parts.length - 1]]
          // Clean up empty objects
          if (Object.keys(current).length === 0 && parts.length > 1) {
            let parent = newErrors
            for (let i = 0; i < parts.length - 2; i++) {
              parent = parent[parts[i]]
            }
            delete parent[parts[parts.length - 2]]
          }
        } else {
          delete newErrors[name]
        }
        return newErrors
      }
      // Use lodash.set for nested paths when setting errors
      const newErrors = cloneDeep(prev)
      set(newErrors, name, error)
      return newErrors
    })
  }, [])

  /**
   * Set touched state for a field
   * @param {string} name - Field name (can be nested)
   * @param {boolean} touchedValue - Whether the field has been touched
   * @param {boolean} shouldValidate - Whether to validate after setting touched (not used currently)
   */
  const setFieldTouched = useCallback((name, touchedValue = true, shouldValidate = false) => {
    setTouched((prev) => {
      const newTouched = { ...prev }
      set(newTouched, name, touchedValue)
      return newTouched
    })
  }, [])

  // Set multiple field values at once
  const setValuesDirect = useCallback((newValues) => {
    setValues(newValues)
  }, [])

  // Set multiple errors at once
  const setErrorsDirect = useCallback((newErrors) => {
    setErrors(newErrors)
  }, [])

  // Set all fields as touched
  const setTouchedAll = useCallback((touchedValue = true) => {
    setTouched((prev) => {
      const allTouched = {}
      const setTouchedRecursive = (obj, path = '') => {
        Object.keys(obj).forEach((key) => {
          const currentPath = path ? `${path}.${key}` : key
          if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
            setTouchedRecursive(obj[key], currentPath)
          } else {
            set(allTouched, currentPath, touchedValue)
          }
        })
      }
      setTouchedRecursive(values)
      return allTouched
    })
  }, [values])

  /**
   * Validate form using a Yup validation schema
   * Sets errors state and returns error object
   * @param {Object} validationSchema - Yup validation schema
   * @returns {Object} Validation errors object (empty if valid)
   */
  const validateForm = useCallback(async (validationSchema) => {
    if (!validationSchema) {
      return {}
    }

    setIsValidating(true)
    try {
      // Use ref to get the latest values, not from closure
      // This ensures we validate with the most current values even if called immediately after setFieldValue
      const currentValues = valuesRef.current
      await validationSchema.validate(currentValues, { abortEarly: false })
      setErrors({})
      setIsValidating(false)
      return {}
    } catch (validationError) {
      const validationErrors = {}
      if (validationError.inner && Array.isArray(validationError.inner)) {
        validationError.inner.forEach((error) => {
          if (error.path) {
            set(validationErrors, error.path, error.message)
          }
        })
      } else if (validationError.path) {
        set(validationErrors, validationError.path, validationError.message)
      }
      setErrors(validationErrors)
      setIsValidating(false)
      return validationErrors
    }
  }, [])

  // Reset form to initial values
  const resetForm = useCallback((newInitialValues) => {
    setValues(newInitialValues || initialValues)
    setErrors({})
    setTouched({})
    setSubmitCount(0)
    setIsSubmitting(false)
  }, [initialValues])

  // Return the latest form values (from ref) so submit handlers see current state immediately after user input
  const getCurrentValues = useCallback(() => valuesRef.current, [])

  // Memoize context value to prevent unnecessary re-renders
  // Only recreate when actual values change, not on every render
  const contextValue = useMemo(() => ({
    values,
    errors,
    touched,
    submitCount,
    isSubmitting,
    isValidating,
    setFieldValue,
    setFieldError,
    setFieldTouched,
    setValues: setValuesDirect,
    setErrors: setErrorsDirect,
    setTouched: setTouchedAll,
    validateForm,
    resetForm,
    getCurrentValues,
    setIsSubmitting,
    setSubmitCount,
  }), [values, errors, touched, submitCount, isSubmitting, isValidating, setFieldValue, setFieldError, setFieldTouched, setValuesDirect, setErrorsDirect, setTouchedAll, validateForm, resetForm, getCurrentValues, setIsSubmitting, setSubmitCount])

  return <FormContext.Provider value={contextValue}>{children}</FormContext.Provider>
}

FormProvider.propTypes = {
  children: PropTypes.node.isRequired,
  initialValues: PropTypes.object,
}

export default FormContext

